import { RoleId, Supervisors } from '@/common/auth/auth.defines';
import { RouteRecordRaw } from 'vue-router';
import { requireAuth } from '@/common/router/authGuard';
import BaseViewport from '@/common/components/templates/baseViewport/BaseViewport.vue';
import { VIEWPORT_TYPE } from '@/common/components/templates/baseViewport/baseViewport.define';
import { extractAccessibleRoutes } from '@/common/auth/auth.utils';
import { VIEW_MODE } from '@/common/define/common.define';
import { ROLE_PERMISSION_KEY } from '@/common/define/rolePermission.define';
// import { isDevServer } from '@/common/router/guard';

const PREFIX_CONFIG = 'config_';

const configMenu: RouteRecordRaw[] = [
  {
    path: '/',
    name: `${PREFIX_CONFIG}Account`,
    component: BaseViewport,
    beforeEnter: requireAuth,
    meta: {
      viewport: VIEWPORT_TYPE.SETTING,
    },
    children: [
      {
        path: 'myInfo',
        name: `${PREFIX_CONFIG}My Info`,
        component: () => import('../views/myInfo/MyInfo.vue'),
      },
      {
        path: 'userGroup',
        name: `${PREFIX_CONFIG}User & Group`,
        component: () => import('../views/userGroup/UserGroup.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_ACCOUNT_USER_USER_GROUP,
          roles: Supervisors,
        },
      },
      {
        path: 'permission',
        name: `${PREFIX_CONFIG}Permission`,
        component: () => import('../views/permission/PermissionView.vue'),
        meta: {
          roles: Supervisors,
        },
      },
    ],
  },
  {
    path: '/platform/',
    name: `${PREFIX_CONFIG}Platform`,
    component: BaseViewport,
    beforeEnter: requireAuth,
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_PLATFORM,
      viewport: VIEWPORT_TYPE.SETTING,
    },
    children: [
      {
        path: 'infrastructure',
        name: `${PREFIX_CONFIG}Infrastructure`,
        component: () => import('../views/platform/infrastructure/ConfigPlatform.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_PLATFORM_INFRASTRUCTURE,
        },
      },
      {
        path: 'cloud',
        name: `${PREFIX_CONFIG}Cloud`,
        component: () => import('../views/platform/cloud/SettingCloud.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_PLATFORM_CLOUD,
          invisibleViewMode: [VIEW_MODE.INTERMAX],
        },
      },
      {
        path: 'kubernetes',
        name: `${PREFIX_CONFIG}Kubernetes`,
        component: () => import('../views/platform/kubernetes/ConfigPlatform.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_PLATFORM_KUBERNETES,
          invisibleViewMode: [VIEW_MODE.MAXGAUGE, VIEW_MODE.INTERMAX],
        },
      },
      {
        path: 'application',
        name: `${PREFIX_CONFIG}Application`,
        component: () => import('../views/platform/application/ConfigPlatform.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_PLATFORM_APPLICATION,
          invisibleViewMode: [VIEW_MODE.MAXGAUGE],
        },
      },
      {
        path: 'database',
        name: `${PREFIX_CONFIG}Database`,
        component: () => import('../views/platform/database/ConfigPlatform.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_PLATFORM_DATABASE,
          invisibleViewMode: [VIEW_MODE.INTERMAX],
        },
      },
      {
        path: 'productIntegration',
        name: `${PREFIX_CONFIG}Product Integration`,
        component: () => import('../views/platform/userIntegration/UserIntegration.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_PLATFORM_USER_INTEGRATION,
        },
      },
    ],
  },
  {
    path: '/monitoring/',
    name: `${PREFIX_CONFIG}Monitoring`,
    component: BaseViewport,
    beforeEnter: requireAuth,
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MONITORING,
      viewport: VIEWPORT_TYPE.SETTING,
    },
    children: [
      {
        path: 'business',
        name: `${PREFIX_CONFIG}Business`,
        component: () => import('../views/monitoring/business/ConfigService.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MONITORING_BUSINESS,
        },
      },
      {
        path: 'log',
        name: `${PREFIX_CONFIG}Log`,
        component: () => import('../views/monitoring/log/LogMonitoringView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MONITORING_LOG,
        },
      },
      {
        path: 'report',
        name: `${PREFIX_CONFIG}Report`,
        component: () => import('../views/monitoring/report/ConfigReport.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MONITORING_REPORT,
        },
      },
      {
        path: 'predefinedReport',
        name: `${PREFIX_CONFIG}Template Report`,
        component: () => import('../views/monitoring/templateReport/TemplateReportView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MONITORING_PREDEFINED_REPORT,
        },
      },
      {
        path: 'externalResource',
        name: `${PREFIX_CONFIG}External Resource`,
        component: () => import('../views/monitoring/externalResource/ConfigPlatform.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MONITORING_EXTERNAL_RESOURCE,
          invisibleViewMode: [VIEW_MODE.MAXGAUGE],
        },
      },
      {
        path: 'externalIntegration',
        name: `${PREFIX_CONFIG}External Integration`,
        component: () => import('../views/monitoring/externalIntegration/ConfigIntegration.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MONITORING_EXTERNAL_INTEGRATION,
          invisibleViewMode: [VIEW_MODE.MAXGAUGE],
        },
      },
    ],
  },
  {
    path: '/alert/',
    name: `${PREFIX_CONFIG}Alert`,
    component: BaseViewport,
    beforeEnter: requireAuth,
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_ALERT,
      viewport: VIEWPORT_TYPE.SETTING,
    },
    children: [
      {
        path: 'userAlert',
        name: `${PREFIX_CONFIG}User Alert`,
        component: () => import('../views/userAlert/UserAlert.vue'),
        beforeEnter: requireAuth,
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_ALERT_USER_ALERT,
        },
      },
      {
        path: 'systemAlert',
        name: `${PREFIX_CONFIG}System Alert`,
        component: () => import('../views/systemAlert/SystemAlert.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_ALERT_SYSTEM_ALERT,
        },
      },
      {
        path: 'externalAlert',
        name: `${PREFIX_CONFIG}External Alert`,
        component: () => import('../views/externalAlert/ExternalAlert.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_ALERT_EXTERNAL_ALERT,
          invisibleViewMode: [VIEW_MODE.MAXGAUGE],
        },
      },
      {
        path: 'notification',
        name: `${PREFIX_CONFIG}Notification`,
        component: () => import('../views/notification/NotificationSetting.vue'),
        // beforeEnter: startLoading,
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_ALERT_NOTIFICATION,
        },
      },
      {
        path: 'alertPreset',
        name: `${PREFIX_CONFIG}Alert Preset`,
        component: () => import('../views/alertPreset/AlertPreset.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_ALERT_PRESET,
        },
      },
    ],
  },
  {
    path: '/assetManagement/',
    name: `${PREFIX_CONFIG}Asset`,
    component: BaseViewport,
    beforeEnter: requireAuth,
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_ASSET,
      viewport: VIEWPORT_TYPE.SETTING,
    },
    children: [
      {
        path: 'eolEos',
        name: `${PREFIX_CONFIG}EOL_EOS`,
        component: () => import('../views/assetManagement/eolEos/ConfigEol.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_ASSET_EOL_EOS,
        },
      },
    ],
  },
  {
    path: '/management/',
    name: `${PREFIX_CONFIG}Management`,
    component: BaseViewport,
    beforeEnter: requireAuth,
    meta: {
      rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_PLATFORM,
      viewport: VIEWPORT_TYPE.SETTING,
    },
    children: [
      {
        path: 'notice',
        name: `${PREFIX_CONFIG}Notice`,
        component: () => import('../views/management/notice/NoticeListView.vue'),
      },
      {
        path: 'agent',
        name: `${PREFIX_CONFIG}Agent`,
        component: () => import('../views/management/agent/AgentView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_AGENT,
          childRolePermission: [
            ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_AGENT_LIST,
            ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_AGENT_PATCH,
            ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_AGENT_PATCH_HISTORY,
          ],
        },
      },
      {
        path: 'license',
        name: `${PREFIX_CONFIG}License`,
        component: () => import('../views/management/license/LicenseView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_LICENSE,
        },
      },
      {
        path: 'tag',
        name: `${PREFIX_CONFIG}Tag`,
        component: () => import('../views/management/tag/SettingTag.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_TAG,
        },
      },
      {
        path: 'security',
        name: `${PREFIX_CONFIG}Security`,
        component: () => import('../views/management/security/SecurityView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_SECURITY,
        },
      },
      {
        path: 'custom-stat',
        name: `${PREFIX_CONFIG}Custom Stat`,
        component: () => import('../views/management/customStat/CustomStatView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_CUSTOM_STAT,
          roles: Supervisors,
        },
      },
      {
        path: 'data',
        name: `${PREFIX_CONFIG}Data`,
        component: () => import('../views/management/data/DataView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_DATA,
          childRolePermission: [
            ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_DATA_RETENTION,
            ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_DATA_METRIC_COLLECTION_CYCLE_MANAGEMENT,
            ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_DATA_STAT,
          ],
        },
      },
      {
        path: 'targetIcon',
        name: `${PREFIX_CONFIG}Target Icon`,
        component: () => import('../views/management/targetIcon/TargetIconView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_TARGET_ICON,
        },
      },
      {
        path: 'customTargetLink',
        name: `${PREFIX_CONFIG}Custom Target Link`,
        component: () => import('../views/management/customTargetLink/CustomTargetLinkView.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_CUSTOM_TARGET_LINK,
          invisibleViewMode: [VIEW_MODE.MAXGAUGE],
        },
      },
      {
        path: 'ai',
        name: `${PREFIX_CONFIG}AI`,
        component: () => import('../views/management/ai/AiView.vue'),
        meta: {
          // 노출·진입을 메뉴 권한으로만 제어 — property(useAI) 가드는 권한 화면에서 끌 수 없어
          // 관리자가 역할별로 조정할 방법이 없었다. URL 직접 접근은 requireAuth 의 menuOnOff 검사가 차단.
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_AI,
        },
      },
      {
        path: 'aiAnomalyDetection',
        name: `${PREFIX_CONFIG}AI Anomaly Detection`,
        component: () => import('../views/aiAnomalyDetection/AiAnomalyDetection.vue'),
        meta: {
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_AI_ANOMALY_DETECTION,
        },
      },
      {
        // chatbot-config-and-usage feature — 챗봇 환경설정 + 토큰 사용량
        path: 'chatbot',
        name: `${PREFIX_CONFIG}Chatbot`,
        component: () => import('../views/management/chatbot/ChatbotConfigView.vue'),
        meta: {
          // 화면 진입은 역할 권한(menuOnOff)으로, 저장 가능 여부는 화면 안 isMaster 분기로 제어
          // (백엔드 ChatbotController 가 쓰기를 Master 전용으로 강제)
          rolePermission: ROLE_PERMISSION_KEY.SETTING.SETTING_MANAGEMENT_AI_ASSISTANT,
        },
      },
    ],
  },
];

const getConfigNaviMenu = (roleId: RoleId) => {
  return extractAccessibleRoutes(configMenu, roleId);
};

export { configMenu, getConfigNaviMenu };
